import { ProfileForm, ProfileFormAnswer } from "../../../../types/Forms";
import { parseFormsTimestamp } from "../../../../utils/formatters";

/**
 * Shared derivations for the Profile Forms tab. The tab consumes the full
 * nested payload from individual/profile/{id}/forms/ (forms > sections >
 * questions > answers), where each answer carries the Individual record it came
 * from and a created_on timestamp. See
 * verify-product/delivery/minor-items/profile-forms.
 */

export interface FormRecord {
  id: number;
  reference: string;
  name: string;
  _type: "individual" | "entity";
  /** Most recent capture time across all this record's answers (millis). */
  latestMillis: number;
  latestCreatedOn: string | null;
}

/**
 * The distinct Individual records that appear across every answer, most
 * recently active first. Each record is one route through the application.
 */
export const deriveRecords = (forms: ProfileForm[]): FormRecord[] => {
  const byId = new Map<number, FormRecord>();

  forms.forEach((form) =>
    form.sections?.forEach((section) =>
      section.questions?.forEach((question) =>
        question.answers?.forEach((answer) => {
          const ind = answer.individual;
          if (!ind) return;
          const millis = parseFormsTimestamp(answer.created_on);
          const existing = byId.get(ind.id);
          if (!existing) {
            byId.set(ind.id, {
              id: ind.id,
              reference: ind.reference,
              name: ind.name,
              _type: ind._type,
              latestMillis: millis,
              latestCreatedOn: answer.created_on,
            });
          } else if (millis > existing.latestMillis) {
            existing.latestMillis = millis;
            existing.latestCreatedOn = answer.created_on;
          }
        })
      )
    )
  );

  return Array.from(byId.values()).sort(
    (a, b) => b.latestMillis - a.latestMillis
  );
};

/** The latest answer a given record gave for a question (null if none). */
export const latestAnswerForRecord = (
  answers: ProfileFormAnswer[] | undefined,
  recordId: number
): ProfileFormAnswer | null => {
  let best: ProfileFormAnswer | null = null;
  let bestMillis = -1;
  (answers ?? []).forEach((answer) => {
    if (answer.individual?.id !== recordId) return;
    const millis = parseFormsTimestamp(answer.created_on);
    if (best === null || millis >= bestMillis) {
      best = answer;
      bestMillis = millis;
    }
  });
  return best;
};

/** Whether a record has at least one answer anywhere in a form. */
export const recordHasAnswersInForm = (
  form: ProfileForm,
  recordId: number
): boolean =>
  form.sections?.some((section) =>
    section.questions?.some((question) =>
      question.answers?.some((answer) => answer.individual?.id === recordId)
    )
  ) ?? false;

/** A question's answers across all records, oldest first. */
export const answersChronological = (
  answers: ProfileFormAnswer[] | undefined
): ProfileFormAnswer[] =>
  [...(answers ?? [])].sort(
    (a, b) => parseFormsTimestamp(a.created_on) - parseFormsTimestamp(b.created_on)
  );

/** Label for a record in the picker: name, reference and last-active date. */
export const recordLabel = (record: FormRecord): string => {
  const date = record.latestCreatedOn
    ? record.latestCreatedOn.split(" ")[0]
    : "";
  return [record.name, record.reference, date].filter(Boolean).join("  ·  ");
};
